"use client";

import { useEffect, useRef } from "react";

export default function ProblemSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible", "animate-fade-up");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left — visual */}
          <div className="relative order-2 lg:order-1">
            <div className="relative rounded-4xl overflow-hidden bg-gradient-to-br from-light-purple to-light-blue p-12 min-h-[400px] flex items-center justify-center">
              {/* Abstract scenario illustration */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-8 left-8 w-32 h-32 rounded-3xl bg-brand-purple" />
                <div className="absolute bottom-8 right-8 w-24 h-24 rounded-full bg-brand-green" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full border-4 border-brand-purple" />
              </div>

              {/* Scenario cards */}
              <div className="relative z-10 space-y-4 w-full max-w-sm">
                {/* Scenario 1 */}
                <div className="bg-white rounded-2xl p-4 shadow-card flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                    <svg width="24" height="24" fill="none" stroke="#EF4444" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                      <polyline points="9 22 9 12 15 12 15 22" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-bold text-sm text-deep-navy">Bought a couch online</p>
                    <p className="text-xs text-body-text">No way to get it home</p>
                  </div>
                  <svg width="20" height="20" fill="#EF4444" viewBox="0 0 24 24" className="ml-auto flex-shrink-0">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" stroke="white" strokeWidth="2" />
                    <line x1="9" y1="9" x2="15" y2="15" stroke="white" strokeWidth="2" />
                  </svg>
                </div>

                {/* Scenario 2 */}
                <div className="bg-white rounded-2xl p-4 shadow-card flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                    <svg width="24" height="24" fill="none" stroke="#F97316" strokeWidth="2" viewBox="0 0 24 24">
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-bold text-sm text-deep-navy">Great marketplace deal</p>
                    <p className="text-xs text-body-text">Passed — too big to carry</p>
                  </div>
                  <svg width="20" height="20" fill="#F97316" viewBox="0 0 24 24" className="ml-auto flex-shrink-0">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" stroke="white" strokeWidth="2" />
                    <line x1="12" y1="16" x2="12.01" y2="16" stroke="white" strokeWidth="2" />
                  </svg>
                </div>

                {/* Solution */}
                <div className="bg-gradient-to-r from-brand-purple to-accent-purple rounded-2xl p-4 shadow-purple-md flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <svg width="24" height="24" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
                      <rect x="1" y="3" width="15" height="13" rx="2" />
                      <path d="M16 8h4l3 5v3h-7V8z" />
                      <circle cx="5.5" cy="18.5" r="2.5" />
                      <circle cx="18.5" cy="18.5" r="2.5" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-bold text-sm text-white">GoGedit to the rescue!</p>
                    <p className="text-xs text-white/70">On-demand delivery, right now</p>
                  </div>
                  <svg width="20" height="20" fill="#4ade80" viewBox="0 0 24 24" className="ml-auto flex-shrink-0">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="9 12 11 14 15 10" stroke="white" strokeWidth="2" fill="none" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Right — copy */}
          <div ref={ref} className="on-scroll order-1 lg:order-2 space-y-8">
            <div className="section-label">The Problem We Solve</div>

            <h2 className="font-heading text-4xl md:text-5xl font-bold text-deep-navy leading-tight">
              Have you ever purchased something and realized that it was too big and you had no way to get it home?
            </h2>

            <p className="text-xl text-body-text font-medium leading-relaxed">
              Skipped on on that great deal because you couldn&apos;t go get it?
            </p>

            <p className="text-body-text leading-relaxed">
              Traditional moving companies can be overkill. Regular couriers aren&apos;t equipped to handle oversized items.
              That gap in the market? That&apos;s exactly where GoGedit lives — connecting you with
              nearby drivers who have the right vehicle, right now.
            </p>

            <div className="grid grid-cols-2 gap-4 pt-4">
              {[
                { icon: "🚫", label: "No moving companies", sub: "Too expensive, too slow" },
                { icon: "📦", label: "No size limits", sub: "Furniture, appliances, more" },
                { icon: "⚡", label: "On-demand", sub: "Available when you need it" },
                { icon: "📍", label: "Real-time tracking", sub: "Know where your stuff is" },
              ]?.map((item) => (
                <div key={item?.label} className="bg-light-blue rounded-2xl p-4">
                  <span className="text-2xl">{item?.icon}</span>
                  <p className="font-bold text-sm text-deep-navy mt-2">{item?.label}</p>
                  <p className="text-xs text-body-text mt-1">{item?.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}